/**
 * SP8 — Empfänger-Auswahl für die Aktionsleiste (Review B20).
 *
 * Baut aus einer `mapContactRow`-Zeile die Empfängerliste für „Mail verfassen"
 * bzw. „Anruf". Das erste Element (falls vorhanden) ist die „Zentrale" — die
 * eigene Default-Kommunikation des Kontakts (bei einer Firma die Firmen-/
 * Zentralnummer, bei einer Person deren eigene Adresse/Nummer).
 *
 * Beim ANRUF stehen danach ALLE wählbaren Nummern: weitere eigene Nummern des
 * Kontakts und je Ansprechpartner jede seiner Nummern, beschriftet mit ihrer
 * Art („Mobil", „Durchwahl"). Faxnummern bleiben draußen. Bei der MAIL bleibt
 * es je Ansprechpartner bei der ersten Adresse.
 *
 * AP-Empfänger gibt es AUSSCHLIESSLICH bei einer **Firma** und dort nur für
 * echte Personen-Ansprechpartner (Beziehungsziel = Person). Damit fallen zwei
 * Fehlerpfade weg (Review B20, Entscheidungen 71/72):
 *   • Firma→Firma-Kaskadenkanten (Tochter-/Schwester-/Mutterfirma) — deren Ziel
 *     ist eine Firma und würde sonst als namenloser „Ansprechpartner" mit der
 *     Konzern-Mail/-Nummer auftauchen (Anruf/Mail an die falsche Firma);
 *   • Person↔Person-Beziehungen (Haushalt/Verwandtschaft, z. B. Ehepartner) —
 *     bei einem Personen-Kunden bleibt es beim direkten Flow (nur eigene
 *     Kommunikation), kein Popover mit Angehörigen.
 *
 * Entscheidungsregel in der UI (KontaktAktionen.tsx):
 *   0 Empfänger → verständliche Meldung (keine Adresse/Nummer hinterlegt),
 *   1 Empfänger → direkter Flow (wie bisher, z. B. Person ohne AP),
 *   ≥2 Empfänger → Empfänger-Popover „Zentrale | AP1 | AP2 …" (Firma mit APs).
 */

export type KontaktAktionMode = "email" | "phone";

export interface Recipient {
    /** Stabiler React-Key. */
    key: string;
    /** Anzeigename: „Zentrale" oder der Name des Ansprechpartners. */
    name: string;
    /** Ziel-Wert: E-Mail-Adresse bzw. Telefonnummer. */
    value: string;
    /** Zusatzinfo (Verantwortlichkeit/Rolle) beim Ansprechpartner. */
    sub?: string;
    /** True für die „Zentrale"-Zeile (eigene Kommunikation des Kontakts). */
    isZentrale?: boolean;
}

/** Verantwortlichkeit bzw. Rolle als Zusatzzeile für einen Ansprechpartner. */
function apSub(rel: any): string | undefined {
    const func = rel?.func != null ? String(rel.func).trim() : "";
    if (func) return func;
    const role = rel?.role;
    if (role && role !== "Unbekannt") return String(role);
    return undefined;
}

/** AP-Name säubern (Fallback, falls kein Name gemappt wurde). */
function apName(rel: any): string {
    const name = rel?.name != null ? String(rel.name).trim() : "";
    return name || "Ansprechpartner";
}

/**
 * Klartext für die Art einer Nummer — „Mobil" neben „Telefon" ist der
 * Unterschied, an dem man im Popover erkennt, welche der beiden man wählt.
 * Ein gepflegtes Label („Durchwahl", „Sekretariat") schlägt den Typ; das
 * automatisch vergebene „Tel 1"/"Tel 2" des Mappers sagt nichts und fliegt raus.
 */
function nummerLabel(eintrag: any): string | undefined {
    const label = String(eintrag?.label ?? "").trim();
    if (label && !/^Tel\s*\d+$/i.test(label)) return label;
    const typ = String(eintrag?.type ?? "").trim();
    if (typ === "mobile") return "Mobil";
    if (typ === "phone") return "Telefon";
    return undefined;
}

/**
 * Ein Fax ist keine Rufnummer für ein Gespräch. Es stand bisher gleichberechtigt
 * in der Liste — und weil je Ansprechpartner NUR die erste Nummer genommen
 * wurde, wählte der Anruf beim falsch sortierten Kontakt das Faxgerät.
 */
function istWaehlbar(eintrag: any): boolean {
    return String(eintrag?.type ?? "") !== "fax";
}

/** Zusatzzeile eines AP-Eintrags: Funktion/Rolle und Art der Nummer. */
function apZusatz(rel: any, mitNummerLabel: boolean): string | undefined {
    const teile = [apSub(rel), mitNummerLabel ? nummerLabel(rel) : undefined].filter(Boolean);
    return teile.length ? teile.join(" · ") : undefined;
}

/**
 * Ist die Beziehung ein echter (Personen-)Ansprechpartner? Ansprechpartner sind
 * immer Personen; ein Firmen-Ziel gehört zur Firma→Firma-Kaskade (Tochter/
 * Schwester/Mutter) und ist KEIN Empfänger für Mail/Anruf des Kunden.
 */
function isApRelation(rel: any): boolean {
    return String(rel?.destTyp ?? "") !== "Firma";
}

/**
 * Empfängerliste (Zentrale + Ansprechpartner) für den gegebenen Modus.
 * Je Ansprechpartner wird nur die erste passende Adresse/Nummer übernommen.
 * Verknüpfte Ansprechpartner werden nur bei einer **Firma** berücksichtigt
 * (Personen-Kunden → nur eigene Kommunikation, direkter Flow).
 */
export function buildRecipients(row: any, mode: KontaktAktionMode): Recipient[] {
    const out: Recipient[] = [];
    const isFirma = String(row?.typ ?? "") === "Firma";

    if (mode === "email") {
        const emails: any[] = Array.isArray(row?.emails) ? row.emails : [];
        const zentrale = emails[0]?.address ?? null;
        if (zentrale) out.push({ key: "zentrale", name: "Zentrale", value: zentrale, isZentrale: true });

        if (isFirma) {
            const rel: any[] = Array.isArray(row?.relationEmails) ? row.relationEmails : [];
            const seen = new Set<string>();
            for (const r of rel) {
                if (!isApRelation(r)) continue;
                const cid = r?.contactId != null ? String(r.contactId) : "";
                const address = r?.address ?? null;
                if (!cid || !address || seen.has(cid)) continue;
                seen.add(cid);
                out.push({ key: `ap-${cid}`, name: apName(r), value: String(address), sub: apSub(r) });
            }
        }
    } else {
        // Telefon zeigt JEDE wählbare Nummer, nicht nur die erste je Person
        // (PO 07.09.2026: „beim Anruf wie vorher die Auswahl und Anzeige der
        // Telefonnummern der AP"). Wer nur die Durchwahl kannte, konnte den
        // Ansprechpartner sonst nicht mobil erreichen — die zweite Nummer war
        // aus dem Vorgang heraus unerreichbar. Doppelte Nummern derselben
        // Person fallen weg, Fax fällt ganz raus.
        const phones: any[] = Array.isArray(row?.phones) ? row.phones : [];
        const eigene = phones.filter((p) => istWaehlbar(p) && p?.number);
        // Die primäre Nummer bleibt vorn — sie ist die „Zentrale".
        eigene.sort((a, b) => Number(!!b?.primary) - Number(!!a?.primary));
        const gesehen = new Set<string>();
        eigene.forEach((p, i) => {
            const nummer = String(p.number);
            if (gesehen.has(nummer)) return;
            gesehen.add(nummer);
            out.push({
                key: `zentrale-${p.id ?? i}`,
                name: "Zentrale",
                value: nummer,
                sub: nummerLabel(p),
                isZentrale: true,
            });
        });

        if (isFirma) {
            const rel: any[] = Array.isArray(row?.relationPhones) ? row.relationPhones : [];
            for (const r of rel) {
                if (!isApRelation(r) || !istWaehlbar(r)) continue;
                const cid = r?.contactId != null ? String(r.contactId) : "";
                const number = r?.number ?? null;
                if (!cid || !number) continue;
                const nummer = String(number);
                const schluessel = `${cid}|${nummer}`;
                if (gesehen.has(schluessel)) continue;
                gesehen.add(schluessel);
                out.push({
                    key: `ap-${cid}-${r?.id ?? nummer}`,
                    name: apName(r),
                    value: nummer,
                    sub: apZusatz(r, true),
                });
            }
        }
    }

    return out;
}
